import { HtmlEditorCommander } from "aldoo-ra/Commander"
import { setCurrentSelectedImage, clearCurrentSelectedImage } from "../align"

const { runCommand } = HtmlEditorCommander

class EventManager {
  constructor(options = {}) {
    this.editorRef = null
    this.readOnly = false
    this.isProcessingContentUpdateRef = null
    this.isPropertyEditorActive = false
    this.variables = {}
    this.onSelectionChange = null
    this.onInput = null
    this.domManager = null

    // Bind methods
    this.initialize = this.initialize.bind(this)
    this.handleClick = this.handleClick.bind(this)
    this.handleKeyDown = this.handleKeyDown.bind(this)
    this.handleInput = this.handleInput.bind(this)
    this.handleSelectionChange = this.handleSelectionChange.bind(this)
    this.handleImageClick = this.handleImageClick.bind(this)
    this.handleButtonClick = this.handleButtonClick.bind(this)
    this.handleLinkClick = this.handleLinkClick.bind(this)

    this.eventListeners = []
  }

  initialize({
    editorRef,
    readOnly,
    isProcessingContentUpdateRef,
    isPropertyEditorActive,
    variables,
    onSelectionChange,
    onInput,
    domManager
  }) {
    this.editorRef = editorRef
    this.readOnly = readOnly
    this.isProcessingContentUpdateRef = isProcessingContentUpdateRef
    this.isPropertyEditorActive = isPropertyEditorActive
    this.variables = variables
    this.onSelectionChange = onSelectionChange
    this.onInput = onInput
    this.domManager = domManager

    this.setupEventListeners()
  }

  setupEventListeners() {
    const editor = this.editorRef?.current
    if (!editor) return

    // Store references to bound methods for cleanup
    this.eventListeners = [
      { element: editor, event: "click", handler: this.handleClick },
      { element: editor, event: "keydown", handler: this.handleKeyDown },
      { element: editor, event: "input", handler: this.handleInput },
      { element: editor, event: "mouseup", handler: this.handleSelectionChange },
      { element: editor, event: "keyup", handler: this.handleSelectionChange },
    ]

    // Add event listeners
    this.eventListeners.forEach(({ element, event, handler }) => {
      element.addEventListener(event, handler)
    })
  }

  handleClick(e) {
    // Check if clicked element is an image
    if (e.target.tagName === "IMG") {
      // Check if this image is inside an editor-button
      const parentButton = e.target.closest(".editor-button")

      if (parentButton) {
        // Image inside button - show image options for the image
        e.preventDefault()
        e.stopPropagation()
        this.handleImageClick(e)
        return
      } else {
        // Standalone image - show image options
        this.handleImageClick(e)
        return
      }
    }

    // Check if clicked element or its parent is an editor-button
    const editorButton = e.target.closest(".editor-button")

    if (editorButton) {
      // Clicked on button (but not on image inside it)
      e.preventDefault()
      e.stopPropagation()

      // Call handleButtonClick with the correct target
      this.handleButtonClick({ target: editorButton })
    } else if (e.target.tagName === "A") {
      this.handleLinkClick(e)
    }
  }

  handleImageClick(event) {
    if (this.readOnly) return

    setCurrentSelectedImage(event.target)
    runCommand("image.transformGizmo", event.target)
    runCommand("image.optionsMenu", {
      selectedImage: event.target,
      variables: this.variables,
      onImageUpdated: (img) => {
        if (this.onInput) this.onInput()
      },
    })
  }

  handleButtonClick(event) {
    if (this.readOnly) return

    // Don't show horizontal menu if we're processing a content update or property editor is active
    if (this.isProcessingContentUpdateRef?.current || this.isPropertyEditorActive) return

    // Check if button contains an image - if so, don't show button menu
    const buttonElement = event.target
    if (
      buttonElement &&
      buttonElement.querySelector &&
      buttonElement.querySelector("img")
    ) {
      // Button contains an image, let the image handler deal with it
      return
    }

    // Only call preventDefault/stopPropagation if the methods exist (for direct calls)
    if (event.preventDefault && typeof event.preventDefault === "function") {
      event.preventDefault()
      event.stopPropagation()
    }

    // Show both horizontal menu and transform gizmo like images
    runCommand("button.transformGizmo", { selectedButton: event.target })
    runCommand("button.horizontalMenu", {
      selectedButton: event.target,
      variables: this.variables,
      onButtonUpdated: (btn) => {
        // Handle button updates if needed
        if (btn) {

        }
      },
    })
  }

  handleLinkClick(event) {
    if (this.readOnly) return
    runCommand("link.edit", { target: event.target, variables: this.variables })
  }

  handleKeyDown(e) {
    if (this.readOnly) return

    if (e.key === "Enter" && e.shiftKey) {
      e.preventDefault()

      // Use DOMManager to handle line break insertion
      if (this.domManager && this.domManager.insertLineBreak()) {
        // Line break was successfully inserted
        if (this.onSelectionChange) {
          this.onSelectionChange()
        }
      }
    } else if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault()

      // Use DOMManager to handle paragraph creation
      if (this.domManager && this.domManager.createNewParagraph()) {
        // Paragraph was successfully created
        if (this.onSelectionChange) {
          this.onSelectionChange()
        }
      }
    }
  }

  handleInput() {
    if (this.readOnly) return

    // Delay the check to allow the input to be processed
    setTimeout(() => {
      // Use DOMManager to ensure paragraph structure
      if (this.domManager) {
        this.domManager.ensureParagraphStructure()
      }

      // Trigger input callback
      if (this.onInput) {
        this.onInput()
      }
    }, 0)
  }

  handleSelectionChange() {
    if (this.onSelectionChange) {
      this.onSelectionChange()
    }
  }

  updateSettings({ readOnly, isPropertyEditorActive, variables }) {
    this.readOnly = readOnly
    this.isPropertyEditorActive = isPropertyEditorActive
    this.variables = variables
  }

  destroy() {
    // Remove all event listeners
    this.eventListeners.forEach(({ element, event, handler }) => {
      if (element) {
        element.removeEventListener(event, handler)
      }
    })

    this.eventListeners = []
  }
}

// Export as a plugin-compatible object
export default {
  create: (options) => new EventManager(options),
  EventManager,
}